import { DateTime } from '@ksyos/date-time';
import { Box, Button, ButtonRow, Col, Link, Modal, Popover, Row, Text, TextInput } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '../../configureStore';
import type { IServiceOrganizationProvider } from '../interfaces/serviceOrganizationProvider';
import type { IExpandedTableRow } from '../userDataManagement/SelectableSortableTable';
import { ServiceType } from '../../../common/enums/serviceType';
import { getDisplayName } from '../../../common/utils/getDisplayName';
import { fetchJson } from '../../store/fetch';
import { getSelectedLanguage } from '../../translate';
import { getServiceOrganizationSetting } from '../../utils/apis/getServiceOrganizationSettings';
import { useAbortableFetch } from '../../utils/hooks/useAbortableFetch';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { SortDirection } from '../../utils/sortDirection';
import {
    addUniqueLocalizedSpecializationText,
    findServiceOrganizationProviders,
    loadPreferredServiceOrganizationsWithServiceProviders,
} from '../pages/personalSettings/preferenceLists/personalSettingsPreferenceListHelper';
import { SelectableSortableTable } from '../userDataManagement/SelectableSortableTable';
import { ListPager } from './ListPager';

const defaultItemsPerPage = 8;

type ServiceOrganizationProviderView = 'preferred' | 'nationWide';

interface IServiceOrganizationProviderSearchInNetworkModalProps {
    initialOrganizationId: string | null;
    initialPractitionerId: string | null;
    itemsPerPage?: number;
    onChange: (selectedOrganization: string | null, practitionerId: string | null) => Promise<void>;
    onClose: () => Promise<void>;
    serviceId: string;
    serviceType?: ServiceType;
}

export function ServiceOrganizationProviderSearchInNetworkModal(
    props: IServiceOrganizationProviderSearchInNetworkModalProps
) {
    const language = useSelector((state: IReduxState) => state.language.code);
    const [serviceOrganizationProviders, setServiceOrganizationProviders] = useState<IServiceOrganizationProvider[]>(
        []
    );
    const [serviceOrganizationProvidersTotal, setServiceOrganizationProvidersTotal] = useState(0);
    const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(props.initialOrganizationId);
    const [selectedPractitionerId, setSelectedPractitionerId] = useState<string | null>(props.initialPractitionerId);
    const [searchOnCityZipCodeOrState, setSearchOnCityZipCodeOrState] = useState('');
    const [
        searchOnServiceProviderNameOrSpecializationOrOrganization,
        setSearchOnServiceProviderNameOrSpecializationOrOrganization,
    ] = useState('');
    const [page, setPage] = useState(1);
    const [isSearching, setIsSearching] = useState(false);
    const [view, setView] = useState<ServiceOrganizationProviderView>('preferred');
    const [expandedOrganizations, setExpandedOrganizations] = useState<string[]>([]);
    const [serviceOrganizationSettingId, setServiceOrganizationSettingId] = useState<string>();
    const [sortKey, setSortKey] = useState('organization.name');
    const [sortDirection, setSortDirection] = useState(SortDirection.asc);
    const abortableFetch = useAbortableFetch();
    const debounce = useDebounce(300);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    const translate = getSelectedLanguage();
    const itemsPerPage = props.itemsPerPage ? props.itemsPerPage : defaultItemsPerPage;

    useEffect(() => {
        if (organizationId && props.serviceId) {
            const getServiceOrganizationSettingForSelectedOrganizationId = async () => {
                const selectedServiceOrganizationSetting = await getServiceOrganizationSetting(
                    organizationId,
                    props.serviceId
                );
                setServiceOrganizationSettingId(selectedServiceOrganizationSetting?.id);
            };
            getServiceOrganizationSettingForSelectedOrganizationId();
        }
    }, [organizationId, props.serviceId]);

    useEffect(() => {
        setServiceOrganizationProviders([]);
        handleSearch();
    }, [view, serviceOrganizationSettingId]);

    useEffect(() => {
        debounce(handleSearch);
    }, [searchOnCityZipCodeOrState, searchOnServiceProviderNameOrSpecializationOrOrganization]);

    useEffect(() => {
        search();
    }, [page, sortDirection, sortKey]);

    return (
        <Modal
            isOpen
            onClose={async () => await props.onClose()}
            size="large"
        >
            <Modal.Header>{translate.personalSettings.preferenceList.searchPreferredAndNationWide}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <ButtonRow>
                    <Button
                        onClick={() => {
                            setSelectedOrganizationId(null);
                            setSelectedPractitionerId(null);
                            setView('preferred');
                        }}
                        size="medium"
                        variant={view === 'preferred' ? 'solid' : 'outline'}
                    >
                        {translate.personalSettings.preferenceList.preferenceList}
                    </Button>
                    <Button
                        onClick={() => {
                            setSelectedOrganizationId(null);
                            setSelectedPractitionerId(null);
                            setView('nationWide');
                        }}
                        size="medium"
                        variant={view === 'nationWide' ? 'solid' : 'outline'}
                    >
                        {translate.personalSettings.preferenceList.nationWide}
                    </Button>
                </ButtonRow>

                <Box
                    mb={6}
                    mt={4}
                >
                    <Row
                        align="center"
                        rowGap={2}
                    >
                        <Col span={[12, null, 5]}>
                            {renderSearchTextInput(
                                'searchOnSpecializationOrOrganization',
                                searchOnServiceProviderNameOrSpecializationOrOrganization,
                                translate.personalSettings.preferenceList.searchOnNameSpecializationOrOrganization,
                                (text) => setSearchOnServiceProviderNameOrSpecializationOrOrganization(text)
                            )}
                        </Col>
                        <Col span={[12, null, 2]}>
                            <Text
                                as="small"
                                key="andOr"
                                size="xs"
                                textAlign="center"
                            >
                                {translate.common.andOr}
                            </Text>
                        </Col>
                        <Col span={[12, null, 5]}>
                            {renderSearchTextInput(
                                'searchOnCityZipCodeOrState',
                                searchOnCityZipCodeOrState,
                                translate.personalSettings.preferenceList.searchOnCityZipCodeOrState,
                                (text) => setSearchOnCityZipCodeOrState(text)
                            )}
                        </Col>
                    </Row>
                </Box>

                <SelectableSortableTable
                    isChildSelectionEnabled={props.serviceType !== ServiceType.referral}
                    isSingleSelection
                    onSelect={async (rowKeys) => handleSelection(rowKeys)}
                    onSort={handleSort}
                    selectedRowKeys={getSelectedRowKeys()}
                    sortDirection={sortDirection}
                    sortKey={sortKey}
                    tableHeaders={getTableHeaders()}
                    tableRows={getTableRows()}
                />

                <ListPager
                    isDisabled={isSearching}
                    itemsPerPage={itemsPerPage}
                    onChangePage={handleNavigation}
                    page={page}
                    showNoResultsText
                    total={serviceOrganizationProvidersTotal}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button
                    className="automation-service-organization-provider-search-in-network-modal-button-choose-organization"
                    isDisabled={selectedOrganizationId === null}
                    onClick={async () => {
                        save();
                    }}
                    variant="solid"
                >
                    {translate.request.chooseOrganization}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    async function search() {
        setIsSearching(true);
        if (view === 'nationWide') {
            const result = await findServiceOrganizationProviders(
                abortableFetch,
                props.serviceId,
                serviceOrganizationSettingId,
                page,
                itemsPerPage,
                searchOnCityZipCodeOrState,
                searchOnServiceProviderNameOrSpecializationOrOrganization,
                sortKey,
                sortDirection,
                true
            );

            setServiceOrganizationProviders(
                addUniqueLocalizedSpecializationText(result.serviceOrganizationProviders, language)
            );
            setServiceOrganizationProvidersTotal(result.total);
        } else if (view === 'preferred') {
            if (serviceOrganizationSettingId) {
                const result = await loadPreferredServiceOrganizationsWithServiceProviders(
                    abortableFetch,
                    serviceOrganizationSettingId,
                    props.serviceId,
                    searchOnCityZipCodeOrState,
                    searchOnServiceProviderNameOrSpecializationOrOrganization,
                    page,
                    itemsPerPage,
                    sortKey,
                    sortDirection,
                    false,
                    true
                );

                setServiceOrganizationProviders(
                    addUniqueLocalizedSpecializationText(result.preferredOrganizations, language)
                );
                setServiceOrganizationProvidersTotal(result.total);
            }
        }

        setIsSearching(false);
    }

    async function handleSearch() {
        if (page === 1) {
            search();
        }
        setPage(1); // if page !== 1 the setPage call with trigger the search
    }

    async function handleNavigation(newPage: number) {
        setPage(newPage);
    }

    async function handleSort(newSortKey: string, newSortDirection: SortDirection) {
        setPage(1);
        setSortKey(newSortKey);
        setSortDirection(newSortDirection);
    }

    function renderSearchTextInput(
        key: string,
        searchText: string,
        placeholder: string,
        onChange: (text: string) => void
    ) {
        return (
            <TextInput
                className="no-ms-clear"
                key={key}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={async (event) => {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        await handleSearch();
                    }
                }}
                onReset={() => onChange('')}
                placeholder={placeholder}
                suffix={{
                    icon: 'search',
                    labelText: 'search',
                    onSuffixClick: () => handleSearch(),
                }}
                value={searchText}
            />
        );
    }

    function getTableRows() {
        if (props.serviceType === ServiceType.referral || props.serviceType === ServiceType.referralSmall) {
            return serviceOrganizationProviders.map((provider) => {
                const applicationWaitingTime = provider.organization.applicationWaitingTime;
                const treatmentWaitingTime = provider.organization.treatmentWaitingTime;
                const providerSpecializationDescriptions = provider.specializationDescriptions?.join(', ');

                return {
                    cells: [
                        { children: provider.organization.name, style: { fontWeight: 500 } },
                        { children: provider.organization.city },
                        {
                            children:
                                typeof applicationWaitingTime === 'number'
                                    ? `${applicationWaitingTime} ${
                                          applicationWaitingTime === 1
                                              ? translate.waitingTime.week
                                              : translate.waitingTime.weeks
                                      }`
                                    : '-',
                        },
                        {
                            children:
                                typeof treatmentWaitingTime === 'number'
                                    ? `${treatmentWaitingTime} ${
                                          treatmentWaitingTime === 1
                                              ? translate.waitingTime.week
                                              : translate.waitingTime.weeks
                                      }`
                                    : '-',
                        },
                        {
                            children:
                                providerSpecializationDescriptions && providerSpecializationDescriptions.length > 30 ? (
                                    <Popover content={providerSpecializationDescriptions}>
                                        {`${providerSpecializationDescriptions.substring(0, 30)}...`}
                                    </Popover>
                                ) : (
                                    providerSpecializationDescriptions
                                ),
                        },
                    ],
                    expandedTableRows: undefined,
                    rowKey: provider.organization.id,
                };
            });
        } else {
            return serviceOrganizationProviders.map((serviceOrganizationProvider) => {
                return {
                    cells: [
                        { children: serviceOrganizationProvider.organization.name, style: { fontWeight: 500 } },
                        { children: serviceOrganizationProvider.organization.city },
                        {},
                        {},
                    ],
                    expandedTableRows: getExpandedTableRows(serviceOrganizationProvider),
                    isNotSelectable:
                        !serviceOrganizationProvider.organization.isRequestRecipient ||
                        allServiceProvidersInOrganizationAreAbsent(serviceOrganizationProvider),
                    rowKey: serviceOrganizationProvider.organization.id,
                };
            });
        }
    }

    function getExpandedTableRows(serviceOrganizationProvider: IServiceOrganizationProvider) {
        const serviceProvidersToShow = expandedOrganizations.includes(serviceOrganizationProvider.organization.id)
            ? serviceOrganizationProvider.serviceProviders
            : serviceOrganizationProvider.serviceProviders.slice(0, 4);

        const expandedTableRows: IExpandedTableRow[] = serviceProvidersToShow.map((serviceProvider) => {
            return {
                cells: [
                    {
                        children: `${getDisplayName(serviceProvider.employment.practitioner)} ${
                            serviceProvider.absence?.endDate
                                ? `(${translate.request.absentUpToAndIncluding} ${DateTime.from(
                                      serviceProvider.absence.endDate
                                  ).format('DD-MM-YYYY')})`
                                : ''
                        }`,
                    },
                    {},
                    {
                        children: serviceProvider.employment.profession
                            ? translate.professionEnum[serviceProvider.employment.profession]
                            : '',
                    },
                    { children: serviceProvider.specializationDescriptions?.join(', ') },
                ],
                isNotSelectable: !serviceProvider.isRequestRecipient || !!serviceProvider.absence,
                rowKey: `${serviceOrganizationProvider.organization.id}_${serviceProvider.employment.practitioner.id}`,
            };
        });

        const index = expandedOrganizations.indexOf(serviceOrganizationProvider.organization.id);
        if (index !== -1) {
            expandedTableRows.push({
                cells: [
                    {
                        children: (
                            <Link
                                onClick={() => {
                                    const updatedExpandedOrganizations = [...expandedOrganizations];
                                    updatedExpandedOrganizations.splice(index, 1);
                                    setExpandedOrganizations(updatedExpandedOrganizations);
                                }}
                                variant="button"
                            >
                                {translate.common.showLess}
                            </Link>
                        ),
                    },
                    {},
                ],
                isNotSelectable: true,
                rowKey: `showLess_${serviceOrganizationProvider.organization.id}`,
            });
        } else if (serviceProvidersToShow.length < serviceOrganizationProvider.serviceProviders.length) {
            expandedTableRows.push({
                cells: [
                    {
                        children: (
                            <Link
                                onClick={() =>
                                    setExpandedOrganizations([
                                        ...expandedOrganizations,
                                        serviceOrganizationProvider.organization.id,
                                    ])
                                }
                                variant="button"
                            >
                                {translate.common.showMore}
                            </Link>
                        ),
                    },
                    {},
                ],
                isNotSelectable: true,
                rowKey: `showMore_${serviceOrganizationProvider.organization.id}`,
            });
        }

        return expandedTableRows;
    }

    async function save() {
        if (view === 'nationWide') {
            await fetchJson('/api/preferredServiceOrganizationProviders', 'POST', {
                organizationId: selectedOrganizationId,
                serviceOrganizationSettingId: serviceOrganizationSettingId,
            });
        }

        await props.onChange(selectedOrganizationId, selectedPractitionerId);
    }

    function getTableHeaders() {
        if (props.serviceType === ServiceType.referral) {
            return [
                {
                    children: translate.organization.organizationSpecialist,
                    columnKey: 'organization.name',
                    isSortable: true,
                },
                { children: translate.common.city, columnKey: 'organization.city', isSortable: true },
                {
                    children: translate.waitingTime.applicationWaitingTime,
                    columnKey: 'organization.applicationWaitingTime',
                },
                {
                    children: translate.waitingTime.treatmentWaitingTime,
                    columnKey: 'organization.treatmentWaitingTime',
                },
                {
                    children: translate.personalSettings.preferenceList.specialization,
                    columnKey: 'organization.specialization',
                },
            ];
        } else {
            return [
                {
                    children: translate.organization.organizationSpecialist,
                    columnKey: 'organization.name',
                    isSortable: true,
                },
                { children: translate.common.city, columnKey: 'organization.city', isSortable: true },
                {
                    children: translate.personalSettings.preferenceList.specialistProfession,
                    columnKey: 'organization.profession',
                },
                {
                    children: translate.personalSettings.preferenceList.specialization,
                    columnKey: 'organization.specialization',
                },
            ];
        }
    }

    function handleSelection(rowKeys: string[]) {
        const parts = rowKeys[0].split('_');
        const selectedOrganization = parts[0];
        const practitionerId = parts[1];

        if (selectedOrganization) {
            setSelectedOrganizationId(selectedOrganization);
        }

        if (practitionerId) {
            setSelectedPractitionerId(practitionerId);
        } else {
            setSelectedPractitionerId(null);
        }
    }

    function getSelectedRowKeys() {
        if (selectedOrganizationId && selectedPractitionerId) {
            return [`${selectedOrganizationId}_${selectedPractitionerId}`];
        } else if (selectedOrganizationId) {
            return [selectedOrganizationId];
        } else {
            return [];
        }
    }

    function allServiceProvidersInOrganizationAreAbsent(serviceOrganizationProvider: IServiceOrganizationProvider) {
        return (
            serviceOrganizationProvider.serviceProviders.filter((serviceProvider) => serviceProvider.absence).length ===
            serviceOrganizationProvider.serviceProviders.length
        );
    }
}
